import {fetchLocations} from "../factories/locations";
import {ADOPTION_TARGET} from "./consts";
import {
  DISTRICT_80_PERCENT_ADOPTION_PLUS_E1,
  DISTRICT_HAS_REACHED_80_PERCENT_ADOPTION, E1_PLUS_ANY_FACILITY_HAS_REACHED_80_PERCENT_ADOPTION,
  FACILITY_A_HAS_REACHED_80_PERCENT_ADOPTION,
  FACILITY_B_HAS_REACHED_80_PERCENT_ADOPTION, FACILITY_C_HAS_REACHED_80_PERCENT_ADOPTION,
  pureActions
} from "../factories/actions";
import {testStakeholderIsStaff, totalWeeksRequired} from "./tools";

const defaultConfig = {
  summary: false,
};

export default class ActionResults {
  constructor(action, state, config = {}) {
    this.action = action;
    this.state = state;
    this.config = {...defaultConfig, ...config};
  }

  /**
   *
   * @returns {string}
   */
  getMessage() {
    const state = this.state;
    switch (this.action.id) {
      case 'I1': {
        const summary = "You conduct meetings with the district health team, health facility managers, vaccinators, and community leaders to discuss the change.";
        if (this.config.summary) return summary;
        return `
        <p class="lg:leading-tight">${summary} This has revealed additional information about:</p>
        <ul class="list-disc pl-5 mt-6">
          <li>
            <strong>Motivation:</strong> willingness and enthusiasm to implement the change.
            <figure class="my-4">
              <div
                class="transform scale-125 origin-left bg-brand-gray-300 rounded-full p-px border-white border w-24">
                <div class="bg-brand-orange-500 rounded-full p-1" style="width: 20%">
                </div>
              </div>
            </figure>
          </li>
          <li>
            <strong>Ability:</strong> knowledge, skills, and authority to effectively implement the change.
            <figure class="my-4">
              <div
                class="transform scale-125 origin-left bg-brand-gray-300 rounded-full p-px border-white border w-24">
                <div class="bg-brand-teal-500 rounded-full p-1" style="width: 80%">
                </div>
              </div>
            </figure>
          </li>
        </ul>
        <p>
          Select each person or group to view additional information.
        </p>`;
      };

      case 'D1':
        return this._makeMessageHTML(`You meet with the facility managers to analyze their local data together.<br/><br/>You can view the results of these meetings by selecting each manager in the Stakeholders view.`);

      case 'D2':
        return this._makeMessageHTML("You meet with the district management team to discuss the results of the facility-level data analysis. Seeing data on the number of unreached children seems to have built some additional buy-in for the change.");

      case 'C1': {
        const summary = "With the rest of the change management team, you plan communication options tailored to these locations, using the change stories that you created earlier.";
        if (this.config.summary) return summary;
        const extra = this._makeRepeatableMessage();
        return this._makeResultsMessage(summary, extra)
          + this._makeRevealedActionsMessage("You will likely not have time to take all of these actions, and some may help more than others.  Choose the actions that you feel like will have the most positive impact.")
          + this._makeOtherMessages();
      }

      case 'C2':
        return this._makeMessageHTML("In the next newsletter, you include quotes and examples that show the potential for positive impact of the quality improvement teams. Even though quality improvement teams are new to this country, there are inspiring success stories from nearby countries that show how local problem solving can help reach additional children with routine immunization services. ");

      case 'C3':
        return this._makeMessageHTML("You hold a brief, example-focused presentation about quality improvement teams at the next quarterly meeting for health facility managers. Following the presentation, you ask health managers to write a 'change story' of how these teams could improve health services at their facilities. This inspires a lively discussion among managers.");

      case 'C4':
        return this._makeMessageHTML("The district team visits another district in a neighboring country, where a similar facility-level quality improvement program has been successfully implemented. They visit the district office and several health facilities, observing both supportive supervision and the quality improvement teams at work.");

      case 'C5':
        return this._makeMessageHTML("You conduct a meeting with the district health team to discuss the change and how it might benefit them. As part of this meeting, you work together with each team member to write a 'change story' of how this change could help their district better reach all children with routine immunization services.");

      case 'C6':
        return this._makeMessageHTML("Together at each facility, you identify and contact community leaders who might be able to assist with planning immunization services. ");

      case 'T1': {
        const summary = "Earlier, the change management team identified the core competencies required for health workers at both the district level and the health facility level.";
        if (this.config.summary) return summary;
        const message = `
        <p class="lg:leading-tight my-6">${summary}</p>
        <ul class="list-disc pl-5 mt-6">
          <li>
            All health workers will need a clear understanding of how, when, and why to use problem-solving techniques and tools.
          </li>
          <li>
            District-level health workers will additionally need to be able to lead and develop others during this change, through regular supportive supervision.
          </li>
          <li>
            Facility-level managers will need additional skills in leadership, management, and accountability.
          </li>
          <li>
            Facility-level health managers and vaccinators will need to be able to engage the community in planning and problem solving.
          </li>
        </ul>
        <p class="lg:leading-tight mt-6">
          The team develops several proposals for training and coaching to meet these needs.
        </p>`;
        const extra = this._makeRepeatableMessage();
        return this._makeResultsMessage(message, extra)
          + this._makeRevealedActionsMessage("You may not have time to take all of these actions, and some may help more than others. Choose the actions that you feel like will have the most positive impact.")
          + this._makeOtherMessages();
      }

      case 'T2':
        return this._makeMessageHTML("Reception of the training is mixed. While district managers acknowledge that there was some useful information provided, they say that it was hard to connect the material from the presentations to their real world tasks. Cascade training of the health facility staff by district managers has similarly mixed reception. ");

      case 'T3':
        return this._makeMessageHTML("You and the rest of the change management team plan training to be integrated into the launch of the quality improvement teams. You will know more about the results once you launch the teams. ");

      case 'T4':
        return this._makeMessageHTML("The workshop-based trainings are well received. You are able to offer advice and practice specific to each location’s challenges, resulting in practical skill-building and useful supporting knowledge. ");

      case 'T5':
        return this._makeMessageHTML("A member of your change team with experience with supportive supervision agrees to provide monthly coaching for district managers, to help improve their skills and troubleshoot challenges together. District managers seem to welcome the introduction of this program.");

      case 'T6':
        return this._makeMessageHTML("The competencies checklists outline the key areas that should be discussed and assessed at each supportive supervision visit. They seem well received. ");

      case 'T7':
        return this._makeMessageHTML("The job aids describe methods and ideas to help the staff better engage with the community, including community leaders who may be able to help with planning and support of immunization sessions. They seem well received. ");

      case 'E1': {
        // If selected without first selecting T2
        const conditionI = state.actions.collection.findIndex(each => each.id === 'T2') < 0;
        let message = `
        In general, the initial problem-solving iterations are going well.<br/><br/>
        The workshop-based trainings included as part of the launch were well received. You are able to offer advice and practice specific to each location’s challenges, resulting in practical skill-building and useful supporting knowledge.<br/><br/>
        You can read more about the results at each location in the Stakeholders view.`;
        if (conditionI) {
          message = `
          Health facilities form quality improvement teams and engage in iterative problem solving, but the results are mixed.<br/><br/>
          Some additional planning and support might improve the situation.<br/><br/>
          You can read more about the results at each location in the Stakeholders view.`;
        }
        return this._makeMessageHTML(message);
      }

      case 'E2':
        return this._makeMessageHTML("You give all health staff a small gift to celebrate the start of the quality improvement teams. ");

      case 'E3':
        return this._makeMessageHTML("You send a message celebrating the initial successes of the quality improvement teams, including specific examples of what has been going well. ");

      case 'E4':
        return this._makeMessageHTML("You publically recognize facility A, whose quality improvement team has been meeting regularly. The staff at this facility seems pleased with the recognition, and other facilities seem eager to catch up.");

      case 'E5':
        return this._makeMessageHTML("You publically recognize facility B, whose quality improvement team has been meeting regularly. The staff at this facility seems pleased with the recognition, and other facilities seem eager to catch up.");

      case 'E6':
        return this._makeMessageHTML("You publically recognize facility C, whose quality improvement team has been meeting regularly. The staff at this facility seems pleased with the recognition, and other facilities seem eager to catch up.");

      case 'R1':
        return this._makeMessageHTML("You send a message congratulating all locations for their ongoing progress, including specific examples of what has been going well.");

      case 'R2':
        return this._makeMessageHTML("District managers are using supportive supervision visits to discuss the benefits of the change with the facilities. These meetings have also been an opportunity to troubleshoot problems together. Staff seems more motivated.");

      case 'R3':
        return this._makeMessageHTML("The regional manager discusses the change with the district managers, addressing their concerns and highlighting how the change will benefit them. They seem more motivated.");

      case 'R4':
        return this._makeMessageHTML("The email from the MoH does not go over well. District managers already feel like their days are full, and they resent being told to work harder. ");

      case 'R5':
        return this._makeMessageHTML("The presentations go well, and facility managers seem to learn from each other’s challenges and successes. ");

      case 'TP1':
        return this._makeMessageHTML("The conversation does not go well. The stakeholder seems even more resistant to the change.");

      case 'TP2':
        return this._makeMessageHTML("The EPI focal point plans an extra supportive supervision visit at the facility. Together, he and the staff practice problem-solving skills together, using the actual challenges of the facility.");

      case 'TP3':
        return this._makeMessageHTML("The extra support is appreciated and seems to be building skills and motivation.");

      case 'TP4':
        return this._makeMessageHTML("The staff from the facility is impressed by what they see and have ideas for how to implement similar strategies at their own facility.");

      case 'TP5': {
        if (this.action.action_results.length > 0) {
          const stakeholder = state.action.stakeholder;
          let message = "While this stakeholder still has some reservations about the change, overall the conversation goes well. The project sponsors’ commitment to the change seems to have a positive effect.";
          if (stakeholder && stakeholder.motivation >= 40) {
            message = "Overall the conversation goes well. The project sponsors’ commitment to the change seems to deepen this stakeholder’s commitment as well.";
          }
          return this._makeMessageHTML(message);
        }
        return '';
      }

      case 'TP6': {
        if (this.action.action_results.length > 0) {
          const location = state.action.location;
          const community = location && location.stakeholders.find(each => each.title === 'Community');
          let message = "You meet with facility staff discuss together to how to continue to increase this community’s participation in the quality improvement teams.";
          if (community && community.participation < 60) {
            message = "Since the community has already started participating in quality improvement teams, you meet with facility staff and discuss how to increase this participation, and make the most of problem solving together. You also discuss the possibility of reaching out to additional community leaders.";
          }
          if (community && community.participation < 30) {
            message = "You meet with facility staff and assess together who is most likely to influence the communities that they are trying to reach. The facility manager reaches out to several community leaders, and one agrees to begin attending quality improvement team meetings.";
          }
          return this._makeMessageHTML(message);
        }
        return '';
      }

      case 'TP7':
        return this._makeMessageHTML("You meet with this stakeholder, discuss what additional skills are needed, and then work together to practice and learn. \n");

      default:
        return '';
    }
  }

  /**
   *
   * @param message
   * @returns {string}
   * @private
   */
  _makeMessageHTML(message) {
    if (this.config.summary) {
      return message;
    }
    const extra = this._makeRepeatableMessage();
    return this._makeResultsMessage(message, extra)
      + this._makeRevealedActionsMessage()
      + this._makeOtherMessages();
  }

  /**
   *
   * @param message
   * @param extra
   * @returns {string}
   * @private
   */
  _makeResultsMessage(message, extra) {
    return `
      <p class="font-semibold text-3xl">
        Results:
      </p>
      <p class="lg:leading-tight mb-6">
        ${message}
      </p>
      ${extra ? '<p class="lg:leading-tight mb-6">'+extra+'</p>' : ''}`;
  }

  /**
   *
   * @returns {string}
   * @private
   */
  _makeOtherMessages() {
    const messages = [];
    const locations = this.state.locations.collection;
    const history = this.state.actions.history.slice();
    const nextHistory = [...history, this.action];
    const nextLocations = fetchLocations(nextHistory, history, 1);
    const adoptionTargetFilter = each => each.adoption >= ADOPTION_TARGET;

    locations.forEach((each, index, collection) => {
      const next = nextLocations.find(item => item.id === each.id);
      const community = each.stakeholders.find(each => Object.keys(each).includes('participation'));
      const nextCommunity = next.stakeholders.find(each => Object.keys(each).includes('participation'));

      // SM2
      // Message shown the first time a facility begins to have adoption and community participation increase
      const conditionI = each.adoption === 0 && next.adoption > 0;
      const conditionII = community && nextCommunity && community.participation < nextCommunity.participation;
      if (conditionI && conditionII) {
        messages.push(`
        <p class="font-semibold text-lg lg:text-2xl">
          Adoption Increasing at ${each.name}
        </p>
        <p class="lg:leading-tight mb-6">
          As quality improvement teams gain momentum at a facility, you will see an increase in adoption and community participation. <br/><br/>
          You can see this currently at ${each.name}.<br/><br/>
          However, do not assume that things will automatically continue to improve! If motivation decreases, adoption and community participation may also decline.
        </p>`)
      }

      // SM3
      // Message shown the first time adoption and community participation decrease at a facility
      if (next.adoption < each.adoption
        && community && nextCommunity
        && nextCommunity.participation < community.participation) {
        messages.push(`
        <p class="font-semibold text-lg lg:text-2xl">
          Adoption Decreasing at ${location.name}
        </p>
        <p class="lg:leading-tight mb-6">
          Motivation at this facility is dropping, leading to a decrease in adoption and community participation. 
        </p>`);
      }

      // SM4
      // Message when the first facility reaches 80% adoption
      const conditionIII = each.adoption < ADOPTION_TARGET && next.adoption >= ADOPTION_TARGET;
      const conditionIV = nextLocations.filter(item => item.id !== each.id).findIndex(adoptionTargetFilter) < 0;
      if (conditionIII && conditionIV) {
        let unlockedActions = [];
        // SM1
        // Message when district level reaches 80% adoption
        if (each.id === "DHT") {
          messages.push(`
            <p class="font-semibold text-lg lg:text-2xl">
              80% Adoption by District Team
            </p>
            <p class="lg:leading-tight mb-6">
              The district team has reached 80% adoption. This means that they are now regularly engaging in active supportive supervision to assist the facility level with problem solving and micro planning<br><br> 
              Your goal is for all locations to reach an adoption level of at least 80%. Keep up the good work!
            </p>
          `);
          unlockedActions = pureActions
            .filter(each => {
              if (each.unlocked_by === DISTRICT_80_PERCENT_ADOPTION_PLUS_E1) {
                return nextHistory.findIndex(i => i.id === "E1") >= 0;
              }
              return each.unlocked_by === DISTRICT_HAS_REACHED_80_PERCENT_ADOPTION;
            });
        } else {
          messages.push(`
          <p class="font-semibold text-lg lg:text-2xl">
            80% Adoption at ${each.name}
          </p>
          <p class="lg:leading-tight mb-6">
            ${each.name} has reached an adoption level of 80%. They are now regularly using quality improvement teams for problem solving and more effective micro planning, with the active input of the community. <br><br>
            Even though there is still work to be done to reinforce and sustain the change, you and they should be proud of the progress you’ve made thus far.<br><br>
            Your goal is for all locations to reach an adoption level of at least 80%. 
          </p>`);
          unlockedActions = pureActions
            .filter(each => {
              if (each.unlocked_by === E1_PLUS_ANY_FACILITY_HAS_REACHED_80_PERCENT_ADOPTION) {
                return nextHistory.findIndex(i => i.id === "E1") >= 0;
              }
              if (each.id === 'FacilityA') {
                return each.unlocked_by === FACILITY_A_HAS_REACHED_80_PERCENT_ADOPTION;
              }
              if (each.id === 'FacilityB') {
                return each.unlocked_by === FACILITY_B_HAS_REACHED_80_PERCENT_ADOPTION;
              }
              if (each.id === 'FacilityC') {
                return each.unlocked_by === FACILITY_C_HAS_REACHED_80_PERCENT_ADOPTION;
              }
            });
        }
        if (unlockedActions.length > 0) {
          messages.push(`
            <p class="lg:leading-tight mt-6 font-semibold text-lg lg:text-2xl">
              This has revealed the following new actions, available in the Actions view:
            </p>
            <ul class="list-disc pl-5 mb-5">
              ${unlockedActions.map(each => `<li>${each.action}</li>`).join('')}
            </ul>
          `);
        }
      }
    });

    // SM5
    // Message when additional facilities reach 80% adoption
    const conditionV = locations.filter(adoptionTargetFilter).length > 0;
    const conditionVI = locations.filter(adoptionTargetFilter).length < nextLocations.filter(adoptionTargetFilter).length;
    if (conditionV && conditionVI) {
      const location = nextLocations.filter(adoptionTargetFilter)
        .find(each => locations.filter(adoptionTargetFilter).findIndex(i => i.id === each.id) < 0);
      if (location) {
        messages.push(`
        <p class="font-semibold text-lg lg:text-2xl">
          80% Adoption at ${location.name}
        </p>
        <p class="lg:leading-tight mb-6">
          ${location.name} has reached an adoption level of 80%. Your goal is for all locations to reach an adoption level of at least 80%. Keep up the good work! 
        </p>`)
      }
    }

    // SM6
    // Pop up shown the when motivation is reduced
    const filteredLocations = nextLocations.filter((each, i) => {
      const staff = each.stakeholders.filter(testStakeholderIsStaff);
      return staff.some(item => item.prev_motivation > item.motivation)
    });
    if (filteredLocations.length > 0) {
      messages.push(`
      <p class="font-semibold text-lg lg:text-2xl">
        Motivation Decreasing
      </p>
      <p class="lg:leading-tight mb-6">
        Change stakeholder motivation is dropping at some locations. Be sure that you are regularly celebrating “quick wins” and sharing examples of successes.
      </p>
      <ul class="list-disc pl-5 mt-6">
        ${filteredLocations.map(each => `<li>Staff at ${each.name} is less motivated</li>`).join('')}
      </ul>`);
    }

    // SM7
    // Message shown 14 weeks after QITs start meeting
    const e1Index = nextHistory.findIndex(each => each.id === 'E1');
    if (e1Index >= 0) {
      const lookupWeeks = totalWeeksRequired(history.slice(0, e1Index));
      const microplanningLookupWeeks = lookupWeeks + 14;
      if (totalWeeksRequired(history) +1 < microplanningLookupWeeks
        && totalWeeksRequired(nextHistory) +1 >= microplanningLookupWeeks) {
        const dht = nextLocations.find(item => item.id === "DHT");
        let dhtAdoptionMessage = '';
        if (dht.adoption >= ADOPTION_TARGET) {
          dhtAdoptionMessage = "The district health team provided active supportive supervision and assistance the day before and during the micro planning sessions.";
        } else if (dht.adoption < 10) {
          dhtAdoptionMessage = "The district health team sent instructions, but did not directly participate.";
        } else {
          dhtAdoptionMessage = "The district health team sent assistance to help during the meeting.";
        }
        messages.push(`
        <p class="font-semibold text-lg lg:text-2xl mt-3">
          Micro planning
        </p>
        <p class="lg:leading-tight mb-6">
          The quality improvement teams had a special meeting to create micro plans for the upcoming year. <br/><br/>
          ${dhtAdoptionMessage}<br/><br/>
          You can read more about the specific results by selecting each facility in the Stakeholders view.
        </p>`)
      }
    }

    return messages.join('');
  }

  /**
   *
   * @param extra
   * @returns {string}
   * @private
   */
  _makeRevealedActionsMessage(extra = '') {
    const unlockedActions = pureActions.filter(each => each.unlocked_by === this.action.id);
    if (unlockedActions.length > 0) {
      const message = `
        <p class="lg:leading-tight mt-6 font-semibold text-lg lg:text-2xl">
          This action has revealed the following new actions, available in the Actions view:
        </p>
        <ul class="list-disc pl-5 mb-5">
          ${unlockedActions.map(each => `<li>${each.action}</li>`).join('')}
        </ul>
      `;
      return extra ? message + `<p class="lg:leading-tight mt-6">${extra}</p>` : message;
    }
    return '';
  }

  /**
   *
   * @returns {string}
   * @private
   */
  _makeRepeatableMessage() {
    if (typeof this.action.repeat_in_weeks === 'number') {
      return this.action.repeat_in_weeks > 1
        ? `This action is repeatable, and it will be available again in ${this.action.repeat_in_weeks} weeks. `
        : "This action is repeatable, so it is still available in the Actions view."
    }
    return '';
  }
}

